import { RobotDesign } from '@/scenes/WorkshopScene';
import { SlotManager, SavedDesign, DesignMode } from '@/systems/SlotManager';

// Re-export SavedDesign so existing imports still work
export type { SavedDesign, DesignMode } from '@/systems/SlotManager';

// ── Singleton design library ──
// Persists across scenes and sessions via SlotManager
class DesignStoreClass {
  private designs: SavedDesign[] = [];
  private _mode: DesignMode = 'campaign';

  constructor() {
    this.loadFromSlot();
  }

  /** Set the active mode — all add/getForMode calls use this */
  set mode(m: DesignMode) { this._mode = m; }
  get mode(): DesignMode { return this._mode; }

  /** Load designs from the active slot */
  private loadFromSlot(): void {
    const slotData = SlotManager.readSlot(SlotManager.activeSlot);
    this.designs = slotData?.designs ?? [];
  }

  /** Reload designs from the active slot (call after switching slots) */
  reload(): void {
    this.loadFromSlot();
  }

  add(name: string, design: RobotDesign): void {
    this.designs.push({ name, design, mode: this._mode });
    this.persist();
  }

  remove(index: number): void {
    this.designs.splice(index, 1);
    this.persist();
  }

  /** Return ALL designs (unfiltered — for persistence/export) */
  getAll(): SavedDesign[] {
    return this.designs;
  }

  /** Return only designs matching the current mode */
  getForMode(): SavedDesign[] {
    return this.designs.filter(d => (d.mode ?? 'campaign') === this._mode);
  }

  /** Get global index from a mode-filtered index */
  globalIndex(modeIndex: number): number {
    const filtered = this.getForMode();
    if (modeIndex < 0 || modeIndex >= filtered.length) return -1;
    const target = filtered[modeIndex];
    return this.designs.indexOf(target);
  }

  count(): number {
    return this.designs.length;
  }

  /** Count designs for current mode */
  countForMode(): number {
    return this.getForMode().length;
  }

  get(index: number): SavedDesign | undefined {
    return this.designs[index];
  }

  update(index: number, name: string, design: RobotDesign): void {
    if (index >= 0 && index < this.designs.length) {
      const mode = this.designs[index].mode ?? this._mode;
      this.designs[index] = { name, design, mode };
      this.persist();
    }
  }

  findIndexByName(name: string): number {
    return this.designs.findIndex(d => d.name === name && (d.mode ?? 'campaign') === this._mode);
  }

  /** Check if an identical design (same components layout) already exists in current mode */
  hasMatchingDesign(design: RobotDesign): boolean {
    return this.findMatchingDesign(design) >= 0;
  }

  /** Find global index of an identical design (same components layout) in current mode, or -1 */
  findMatchingDesign(design: RobotDesign): number {
    const key = (c: { type: string; lx: number; ly: number; rotation?: number; evoLevel?: number }) =>
      `${c.type}:${c.lx}:${c.ly}:${c.rotation ?? 0}:${c.evoLevel ?? 0}`;
    const sortedB = design.components.map(key).sort();
    return this.designs.findIndex(saved => {
      if ((saved.mode ?? 'campaign') !== this._mode) return false;
      const a = saved.design.components;
      if (a.length !== sortedB.length) return false;
      const sortedA = a.map(key).sort();
      return sortedA.every((k, i) => k === sortedB[i]);
    });
  }

  private persist(): void {
    SlotManager.updateDesigns(this.designs);
  }
}

export const DesignStore = new DesignStoreClass();
